import {
  initLoginSDK,
  loadScript,
  handleAPIWithSlideVerification,
  sendLoginCodeRequest,
} from "./utils.js";
import { styles } from "./styles.js";

class LoginModal extends HTMLElement {
  constructor() {
    super();
    // 移除Shadow DOM，使用普通DOM
    this.activeTab = "phone"; // 默认选中手机验证码登录
    this.countdown = 0;
    this.countdownTimer = null;
    this.agreeTerms = false;
    this.phoneAreaCode = "+86";
    this.areaCodeList = []; // 存储区号列表
    this.isLoadingAreaCodes = false; // 区号加载状态
    this.phoneInput = ""; // 手机号输入值
    this.verificationInput = ""; // 验证码输入值
    this.usernameInput = ""; // 账号输入值
    this.passwordInput = ""; // 密码输入值
    this.showCloseBtn = true; // 是否显示关闭按钮，默认显示
  }

  // 添加静态方法获取观察的属性
  static get observedAttributes() {
    return ["show-close-btn"];
  }

  // 属性变化监听
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "show-close-btn") {
      this.showCloseBtn = newValue !== "false" && newValue !== false;
      // 如果组件已经渲染，重新渲染以应用变化
      if (this.innerHTML) {
        this.render();
      }
    }
  }

  connectedCallback() {
    // 直接在元素上添加事件监听，不再使用shadowRoot
    this.addEventListener("click", (e) => this.handleElementClick(e));

    this.render();
    initLoginSDK().then(() => {
      this.fetchAreaCodes(); // 初始化SDK后获取区号列表
    });

    // 全局事件监听 - 使用箭头函数避免this绑定问题
    document.addEventListener("click", (e) => this.handleDocumentClick(e));

    // 添加调试信息
    console.log("登录组件已加载");
  }

  disconnectedCallback() {
    this.clearCountdown();
    // 移除全局事件监听
    document.removeEventListener("click", this.handleDocumentClick);
    console.log("登录组件已卸载");
  }

  // 修改事件处理方法名
  handleElementClick(event) {
    const target = event.target;
    // 处理遮罩层点击 - 仅当showCloseBtn为true时允许点击遮罩关闭
    // if (target.classList.contains("login-overlay") && this.showCloseBtn) {
    //   console.log("点击遮罩层关闭弹窗");
    //   this.close();
    //   return;
    // }
    // 处理协议复选框
    if (target.classList.contains("agreement-checkbox")) {
      this.handleAgreementChange(event);
      return;
    }

    // 处理标签切换
    if (target.classList.contains("tab")) {
      const tab = target.getAttribute("data-tab");
      if (tab && tab !== this.activeTab) {
        console.log("切换到标签:", tab);
        this.activeTab = tab;
        this.render();
      }
      return;
    }

    // 处理关闭按钮
    if (target.classList.contains("close-btn")) {
      console.log("点击关闭按钮");
      this.close();
      return;
    }

    // 处理获取验证码按钮
    if (target.classList.contains("verification-btn") && !target.disabled) {
      console.log("点击获取验证码");
      this.handleGetVerificationCode();
      return;
    }

    // 处理登录按钮
    if (target.classList.contains("login-btn") && !target.disabled) {
      console.log("点击登录按钮");
      this.handleLogin();
      return;
    }

    // 处理区号选择器
    if (target.closest(".area-code-selector")) {
      console.log("点击区号选择器");
      this.toggleAreaCodeDropdown(event);
      event.stopPropagation();
      return;
    }
  }

  // 处理document点击事件，关闭下拉菜单
  handleDocumentClick(event) {
    // 如果点击在组件之外，关闭所有区号下拉菜单
    const allDropdowns = this.querySelectorAll(".area-code-dropdown");
    allDropdowns.forEach((dropdown) => {
      if (dropdown && dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  }

  // 添加显示错误信息的方法
  showError(elementId, message) {
    const errorElement = this.querySelector(`#${elementId}`);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = "block";

      // 自动清除错误消息
      setTimeout(() => {
        if (errorElement) {
          errorElement.textContent = "";
          errorElement.style.display = "none";
        }
      }, 5000); // 5秒后自动清除
    }
  }

  // 清除错误信息
  clearError(elementId) {
    const errorElement = this.querySelector(`#${elementId}`);
    if (errorElement) {
      errorElement.textContent = "";
      errorElement.style.display = "none";
    }
  }

  // 添加倒计时清理方法
  startCountdown() {
    this.countdown = 60;
    const verificationBtn = this.querySelector(".verification-btn");
    if (verificationBtn) {
      verificationBtn.disabled = true;
      verificationBtn.textContent = `重新获取(${this.countdown}s)`;

      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.clearCountdown();
        } else if (verificationBtn) {
          verificationBtn.textContent = `重新获取(${this.countdown}s)`;
        }
      }, 1000);
    }
  }

  // 清除倒计时计时器和状态
  clearCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    this.countdown = 0;

    const verificationBtn = this.querySelector(".verification-btn");
    if (verificationBtn) {
      verificationBtn.disabled = false;
      verificationBtn.textContent = "获取验证码";
    }
  }

  render() {
    // 使用普通DOM，添加样式到head
    if (!document.querySelector('#login-modal-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'login-modal-styles';
      styleElement.textContent = styles;
      document.head.appendChild(styleElement);
    }

    this.innerHTML = `
      <div class="login-overlay">
       <div class="login-modal">
        <div class="login-header">
          <h2>登录考满分账号</h2>
          ${this.showCloseBtn ? '<span class="close-btn"></span>' : ""}
        </div>
        <div class="login-tabs">
          <div class="tab ${
            this.activeTab === "phone" ? "active" : ""
          }" data-tab="phone">手机验证码登录</div>
          <div class="tab ${
            this.activeTab === "account" ? "active" : ""
          }" data-tab="account">账号密码登录</div>
        </div>
        <div class="login-content">
          ${this.renderTabContent()}
        </div>
        ${this.activeTab === "phone" 
          ? '<p class="login-footer-tips">新手机号将自动进入注册流程</p>' 
          : '<p class="login-footer-tips"><a href="https://account.kmf.com/account/findpass" target="_blank">忘记密码</a></p>'
        }
        <div class="login-footer">
          <button class="login-btn" ${
            this.isLoginButtonDisabled() ? "disabled" : ""
          }>注册/登录</button>
       
        
          <div class="agreement">
            <label>
              <input type="checkbox" class="agreement-checkbox" ${
                this.agreeTerms ? "checked" : ""
              }>
              我已阅读并同意<a href="https://account.kmf.com/agreement?type=Privacy" target="_blank">《考满分用户协议》</a>和<a href="https://account.kmf.com/agreement?type=person" target="_blank">《用户个人信息保护政策》</a>
            </label>
          </div>
            <!-- 滑块验证容器 - 使用真实DOM -->
          <div id="captcha"></div>
          <a href="https://www.kmf.com/static/appcenter" target="_blank" class="other-tips">
             你的帐号通用于考满分旗下网站及所有移动端产品<span></span> 
          </a>
        </div>
      </div>
      </div>
    `;

    // 添加输入框事件监听器，实时更新状态
    this.setupInputListeners();
  }

  // 计算登录按钮是否应该禁用 - 修改为不再检查协议勾选
  isLoginButtonDisabled() {
    // 根据当前标签页判断
    if (this.activeTab === "phone") {
      // 手机号和验证码都必须有值
      return !this.phoneInput || !this.verificationInput;
    } else {
      // 账号和密码都必须有值
      return !this.usernameInput || !this.passwordInput;
    }
  }

  // 添加输入框事件监听
  setupInputListeners() {
    // 手机号输入框
    const phoneInput = this.querySelector(".phone-input");
    if (phoneInput) {
      phoneInput.value = this.phoneInput;
      phoneInput.addEventListener("input", (e) => {
        this.phoneInput = e.target.value.trim();
        this.updateLoginButtonState();
      });
    }

    // 验证码输入框
    const verificationInput = this.querySelector(".verification-input");
    if (verificationInput) {
      verificationInput.value = this.verificationInput;
      verificationInput.addEventListener("input", (e) => {
        this.verificationInput = e.target.value.trim();
        this.updateLoginButtonState();
      });
    }

    // 账号输入框
    const usernameInput = this.querySelector(".username-input");
    if (usernameInput) {
      usernameInput.value = this.usernameInput;
      usernameInput.addEventListener("input", (e) => {
        this.usernameInput = e.target.value.trim();
        this.updateLoginButtonState();
      });
    }

    // 密码输入框
    const passwordInput = this.querySelector(".password-input");
    if (passwordInput) {
      passwordInput.value = this.passwordInput;

      passwordInput.addEventListener("input", (e) => {
        this.passwordInput = e.target.value.trim();
        this.updateLoginButtonState();
      });

      // 添加密码显示/隐藏切换功能
      const passwordToggle = this.querySelector(".password-toggle");
      if (passwordToggle) {
        passwordToggle.addEventListener("click", () => {
          const eyeClose = passwordToggle.querySelector(".eye-close");
          const eyeOpen = passwordToggle.querySelector(".eye-open");

          if (passwordInput.type === "password") {
            passwordInput.type = "text";
            eyeClose.style.display = "none";
            eyeOpen.style.display = "block";
            passwordToggle.setAttribute("aria-label", "隐藏密码");
          } else {
            passwordInput.type = "password";
            eyeClose.style.display = "block";
            eyeOpen.style.display = "none";
            passwordToggle.setAttribute("aria-label", "显示密码");
          }
        });
      }
    }
  }

  // 更新登录按钮状态
  updateLoginButtonState() {
    const loginBtn = this.querySelector(".login-btn");
    if (loginBtn) {
      loginBtn.disabled = this.isLoginButtonDisabled();
    }
  }

  // 修改：Tab切换处理
  handleTabClick(event) {
    const tab = event.currentTarget.getAttribute("data-tab");
    console.log("Tab切换:", tab);
    if (tab && tab !== this.activeTab) {
      this.activeTab = tab;
      this.render();
      // 切换标签后更新登录按钮状态
      this.updateLoginButtonState();
    }
  }

  renderTabContent() {
    if (this.activeTab === "phone") {
      return `
        <div class="phone-login">
          <div class="input-group">
            <div class="area-code-selector">
              <span>${this.getAreaCodeDisplay()}</span>
              <div class="area-code-dropdown">
                ${this.renderAreaCodeOptions()}
              </div>
            </div>
            <input type="tel" class="phone-input" placeholder="请输入手机号">
          </div>
          <div class="error-message" id="phone-error"></div>
          <div class="input-group verification-group">
            <input type="text" class="verification-input" placeholder="请输入验证码">
            <button class="verification-btn" ${
              this.countdown > 0 ? "disabled" : ""
            }>
              ${
                this.countdown > 0
                  ? `重新获取(${this.countdown}s)`
                  : "获取验证码"
              }
            </button>
          </div>
           <div class="error-message" id="verification-error"></div>
        </div>
      `;
    } else {
      // 修改账号密码登录，添加密码显示/隐藏切换按钮
      return `
        <div class="account-login">
          <div class="input-group">
            <div class="area-code-selector">
            <span>${this.getAreaCodeDisplay()}</span>
            <div class="area-code-dropdown">
              ${this.renderAreaCodeOptions()}
            </div>
          </div>
            <input type="text" class="username-input" placeholder="请输入账号">
          </div>
            <div class="error-message" id="username-error"></div>
          <div class="input-group password-group">
            <input type="password" class="password-input" placeholder="请输入密码">
              <button type="button" class="password-toggle" aria-label="显示密码">
              <svg class="eye-icon eye-close" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#999" d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
              </svg>
              <svg class="eye-icon eye-open" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" style="display: none;">
                <path fill="#999" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
              </svg>
            </button>
          </div>
          <div class="error-message" id="password-error"></div>
        </div>
      `;
    }
  }
  // 添加一个辅助方法来获取当前选中区号的显示文本
  getAreaCodeDisplay() {
    // 查找当前选中区号对应的国家/地区名称
    const selectedCode = this.phoneAreaCode.replace("+", "");
    const selectedArea = this.areaCodeList.find(
      (item) => item.code == selectedCode
    );

    // 如果找到匹配项，显示"国家/地区名称 +区号"格式
    if (selectedArea) {
      return `${this.phoneAreaCode}（${selectedArea.national}）`;
    }

    // 否则仅显示区号
    return this.phoneAreaCode;
  }
  renderAreaCodeOptions() {
    if (this.isLoadingAreaCodes) {
      return '<div class="area-code-loading">加载中...</div>';
    }

    if (!this.areaCodeList.length) {
      return '<div class="area-code-empty">暂无区号数据</div>';
    }

    return this.areaCodeList
      .map(
        (item) =>
          `<div class="area-code-option" data-code="+${item.code}">+${item.code}（${item.national}）</div>`
      )
      .join("");
  }

  // 监听协议复选框变化
  handleAgreementChange(event) {
    const checkbox = event.target;
    if (checkbox) {
      this.agreeTerms = checkbox.checked;
      this.updateLoginButtonState();
      console.log("协议同意状态:", this.agreeTerms);
    }
  }

  close() {
    this.dispatchEvent(new CustomEvent("close"));
    if (this.parentNode) {
      this.parentNode.removeChild(this);
    }
  }

  // 修改：区号下拉框切换
  toggleAreaCodeDropdown(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // 获取相应的下拉菜单
    const selectorElement = event.target.closest(".area-code-selector");
    const inputGroup = selectorElement.closest(".input-group");
    const dropdown = inputGroup.querySelector(".area-code-dropdown");

    if (!dropdown) return;

    // 添加选择区号的事件处理
    const options = dropdown.querySelectorAll(".area-code-option");
    options.forEach((option) => {
      option.onclick = (e) => {
        const code = e.currentTarget.getAttribute("data-code");
        const text = e.currentTarget.textContent;

        // 更新区号选择器显示
        const selector = inputGroup.querySelector(".area-code-selector span");
        selector.textContent = text;

        // 保存区号值
        this.phoneAreaCode = code;

        // 隐藏下拉菜单
        dropdown.style.display = "none";
        e.stopPropagation();
      };
    });

    const currentDisplay = window.getComputedStyle(dropdown).display;
    dropdown.style.display =
      currentDisplay === "none" || !currentDisplay ? "block" : "none";

    // 关闭其他所有下拉菜单
    const allDropdowns = this.querySelectorAll(".area-code-dropdown");
    allDropdowns.forEach((item) => {
      if (item !== dropdown) {
        item.style.display = "none";
      }
    });
  }

  // 修改获取验证码方法
  handleGetVerificationCode() {
    const phoneInput = this.querySelector(".phone-input");
    const phone = phoneInput.value.trim();
    this.phoneInput = phone;

    if (!phone) {
      this.showError("phone-error", "请输入手机号");
      return;
    }

    // 清除错误
    this.clearError("phone-error");
    this.startCountdown();

    // 使用通用滑块验证处理方法
    const phoneCode = parseInt(this.phoneAreaCode.replace("+", ""));
    handleAPIWithSlideVerification(
      "sendSMSCodeLogin",
      {
        phone_code: phoneCode,
        phone: phone,
      },
      (res) => {
        console.log("验证码发送成功", res);
      },
      (error) => {
        console.error("验证码发送失败", error);
        this.showError("verification-error", error.errmsg || "验证码发送失败");
        this.clearCountdown();
      }
    );
  }

  // 修改登录方法
  handleLogin() {
    // 在点击登录按钮时检查协议是否勾选
    if (!this.agreeTerms) {
      // 创建错误消息元素
      const agreementWrapper = this.querySelector(".agreement");
      if (agreementWrapper) {
        // 检查是否已有错误消息元素
        let errorElement = agreementWrapper.querySelector(".error-message");

        // 如果没有，则创建一个
        if (!errorElement) {
          errorElement = document.createElement("div");
          errorElement.className = "error-message";
          errorElement.style.display = "block";
          errorElement.style.marginTop = "5px";
          agreementWrapper.appendChild(errorElement);
        }

        errorElement.textContent = "请先同意用户协议和隐私政策";

        // 设置定时器移除错误消息
        setTimeout(() => {
          if (errorElement && errorElement.parentNode) {
            errorElement.parentNode.removeChild(errorElement);
          }
        }, 3000);
      }
      return;
    }

    if (this.activeTab === "phone") {
      const phone = this.querySelector(".phone-input").value.trim();
      const code = this.querySelector(".verification-input").value.trim();

      if (!phone) {
        this.showError("phone-error", "请输入手机号");
        return;
      }

      if (!code) {
        this.showError("verification-error", "请输入验证码");
        return;
      }

      // 清除所有错误
      this.clearError("phone-error");
      this.clearError("verification-error");

      // 使用通用滑块验证处理方法
      const phoneCode = parseInt(this.phoneAreaCode.replace("+", ""));
      handleAPIWithSlideVerification(
        "loginSms",
        {
          phone: phone,
          phone_code: phoneCode,
          sms_code: code,
        },
        async (res) => {
          console.log("登录成功", res);

          // 登录成功后，发送code到后端
          if (res.errcode === 0 && res.data && res.data.code) {
            try {
              const codeRes = await sendLoginCodeRequest(res.data.code);
              console.log("Code请求完成:", codeRes);
              this.dispatchEvent(
                new CustomEvent("login-success", { detail: res })
              );
              this.close();
            } catch (error) {
              console.error("Code请求失败:", error);
              this.showError(
                "verification-error",
                error.errmsg || "登录失败，请重试"
              );
            }
          } else {
            console.warn("登录成功但返回数据中无code字段");
          }
        },
        (error) => {
          console.error("登录失败", error);
          this.showError(
            "verification-error",
            error.errmsg || "登录失败，请重试"
          );
          this.dispatchEvent(new CustomEvent("login-fail", { detail: error }));
        }
      );
    } else {
      const username = this.querySelector(".username-input").value.trim();
      const password = this.querySelector(".password-input").value.trim();

      if (!username) {
        this.showError("username-error", "请输入账号");
        return;
      }

      if (!password) {
        this.showError("password-error", "请输入密码");
        return;
      }

      // 清除所有错误
      this.clearError("username-error");
      this.clearError("password-error");

      // 使用通用滑块验证处理方法
      const phoneCode = this.phoneAreaCode.replace("+", "");
      const symbol = `${phoneCode}-${username}`;

      handleAPIWithSlideVerification(
        "loginPassword",
        {
          symbol: symbol,
          password: password,
        },
        async (res) => {
          console.log("登录成功", res);

          if (res.errcode === 0 && res.data && res.data.code) {
            try {
              const codeRes = await sendLoginCodeRequest(res.data.code);
              console.log("Code请求完成:", codeRes);
            } catch (error) {
              console.error("Code请求失败:", error);
            }
          } else {
            console.warn("登录成功但返回数据中无code字段");
          }

          this.dispatchEvent(new CustomEvent("login-success", { detail: res }));
          this.close();
        },
        (error) => {
          console.error("登录失败", error);
          this.showError(
            "password-error",
            error.errmsg || "登录失败，请检查账号密码"
          );
          this.dispatchEvent(new CustomEvent("login-fail", { detail: error }));
        }
      );
    }
  }

  fetchAreaCodes() {
    if (!window.TAL_UC) {
      console.error("TAL_UC SDK未初始化");
      return;
    }

    this.isLoadingAreaCodes = true;
    this.render(); // 重新渲染以显示加载状态

    window.TAL_UC.getAllPhoneCodeList({
      success: (res) => {
        if (res.errcode === 0 && res.data && res.data.list) {
          this.areaCodeList = res.data.list;
          console.log("区号列表获取成功", this.areaCodeList);
        } else {
          console.error("获取区号列表返回格式错误", res);
          // 设置默认区号列表作为备选
          this.areaCodeList = [
            { national: "中国", code: 86 },
            { national: "中国香港", code: 852 },
            { national: "中国台湾", code: 886 },
            { national: "中国澳门", code: 853 },
          ];
        }
        this.isLoadingAreaCodes = false;
        this.render(); // 更新UI
      },
      fail: (error) => {
        console.error("获取区号列表失败", error);
        // 设置默认区号列表作为备选
        this.areaCodeList = [
          { national: "中国", code: 86 },
          { national: "中国香港", code: 852 },
          { national: "中国台湾", code: 886 },
          { national: "中国澳门", code: 853 },
        ];
        this.isLoadingAreaCodes = false;
        this.render(); // 更新UI
      },
    });
  }
}

// 注册Web Component
customElements.define("login-modal", LoginModal);

// 导出一个可全局调用的方法，用于创建和显示登录弹窗
function showLoginModal(options = {}) {
  const modal = document.createElement("login-modal");

  // 设置配置属性
  if (options.hasOwnProperty("showCloseBtn")) {
    modal.setAttribute("show-close-btn", options.showCloseBtn);
    modal.showCloseBtn = options.showCloseBtn;
  }

  // 添加事件监听
  if (options.onSuccess) {
    modal.addEventListener("login-success", options.onSuccess);
  }

  if (options.onFail) {
    modal.addEventListener("login-fail", options.onFail);
  }

  if (options.onClose) {
    modal.addEventListener("close", options.onClose);
  }

  // 添加到页面
  document.body.appendChild(modal);

  return modal;
}

// 自动挂载到全局 - 修改为直接挂载showLoginModal方法
if (typeof window !== "undefined") {
  window.LoginModal = {
    showLoginModal,
    LoginModalComponent: LoginModal,
  };
  // 直接挂载showLoginModal到window对象
  window.showLoginModal = showLoginModal;
}

// 支持SeaJS - 修改为更兼容的格式
if (typeof define === "function") {
  if (define.amd) {
    // AMD支持
    define(function () {
      return {
        showLoginModal,
        LoginModal,
      };
    });
  } else if (define.cmd) {
    // SeaJS支持
    define(function (require, exports, module) {
      exports.showLoginModal = showLoginModal;
      exports.LoginModal = LoginModal;
      module.exports = showLoginModal; // 默认导出
    });
  }
}

// 支持CommonJS
if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    showLoginModal,
    LoginModal,
  };
}

// 导出组件类和方法
export { LoginModal, showLoginModal };
export default showLoginModal;
